<template>
  <div class="cn-support-form">
    <div v-if="formPending" class="cn-support-form__pending-overlay">
      <UiSpinner />
    </div>
    <div v-if="formSuccess" class="cn-support-form__success">
      <p>{{ $t('success_form_request') }}</p>
    </div>
    <div v-else>
      <form class="cn-support-form__form" @submit.prevent="submitForm">
        <div class="cn-support-form__form-grid">
          <div class="cn-support-form__name">
            <UiInput
              v-model.trim="form.name"
              :placeholder="$t('full_name')"
              :error="errorMessage(v$.name)"
              @update:model-value="v$.name.$touch()"
            />
          </div>
          <div class="cn-support-form__country">
            <UiInput
              v-model="form.country"
              :placeholder="$t('country')"
              :error="errorMessage(v$.country)"
              @update:model-value="v$.country.$touch()"
            />
          </div>
          <div class="cn-support-form__product-type">
            <UiInput
              v-model.trim="form.product_type"
              :placeholder="$t('product_type_and_model')"
              :error="errorMessage(v$.product_type)"
              @update:model-value="v$.product_type.$touch()"
            />
          </div>
          <div class="cn-support-form__email">
            <UiInput
              v-model.trim="form.email"
              :placeholder="$t('email')"
              :error="errorMessage(v$.email)"
              @update:model-value="v$.email.$touch()"
            />
          </div>
          <div class="cn-support-form__comment">
            <UiTextarea
              v-model.trim="form.comment"
              :placeholder="$t('message')"
              :error="errorMessage(v$.comment)"
              @update:model-value="v$.comment.$touch()"
            />
          </div>
          <div class="cn-support-form__agreement">
            <UiCheckbox v-model="form.agreement" class="text-sm">
              <span
                class="cn-support-form__agreement-text text-sm"
                v-html="$t('support_form_agreement')"
              />
            </UiCheckbox>
          </div>
          <UiRecaptcha
            class="cn-support-form__recaptcha"
            @error="captchaWidget.setToken('')"
            @success="captchaWidget.setToken($event)"
            @expired="captchaWidget.setToken('')"
          />
          <div class="cn-support-form__form-controls">
            <UiButton
              variant="red"
              type="submit"
              :disabled="isSubmitDisabled(v$.$invalid || !form.agreement)"
            >
              {{ $t('send') }}
            </UiButton>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useForm, useValidationRules } from '@/composables';
import UiTextarea from '@/components/ui/UiTextarea';
import UiSpinner from '@/components/ui/UiSpinner';
import UiInput from '@/components/ui/UiInput';
import UiCheckbox from '@/components/ui/UiCheckbox';
import UiButton from '@/components/ui/UiButton';
import UiRecaptcha from '@/components/ui/UiRecaptcha';

interface Props {
  success?: boolean;
  pending?: boolean;
}

defineProps<Props>();
const {
  formSuccess,
  formPending,
  submit,
  recaptcha: captchaWidget,
  isSubmitDisabled,
} = useForm({ confirm: false });
const { required, email, errorMessage } = useValidationRules();

const form = ref({
  email: '',
  comment: '',
  country: '',
  name: '',
  product_type: '',
  agreement: false,
});

const rules = {
  name: { required },
  country: { required },
  product_type: { required },
  email: { required, email },
  comment: { required },
};
const v$ = useVuelidate(rules, form);

const { supportFormUuid } = useRuntimeConfig().public.frontkit;
const submitForm = () => {
  submit(form.value, supportFormUuid);
};
</script>

<style lang="scss">
.cn-support-form {
  --ab-canyon-support-form-pending-overlay-color-bg: var(
    --ab-canyon-color-light
  );

  position: relative;
  &__pending-overlay {
    background-color: color-mix(
      in srgb,
      var(--ab-canyon-support-form-pending-overlay-color-bg),
      transparent 50%
    );
    padding-top: calc(4 * var(--size-base));
    padding-bottom: calc(4 * var(--size-base));
    justify-content: center;
    display: flex;
    z-index: 10;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    position: absolute;
  }
  &__success {
    p {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
  }
  &__form-grid {
    gap: var(--size-xl);
    grid-template-columns: repeat(1, minmax(0, 1fr));
    display: grid;
    @media (min-width: 656px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  &__name {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__country {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__product-type {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__email {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__comment {
    grid-column: span 2 / span 2;
  }
  &__agreement {
    grid-column: span 2 / span 2;
  }
  &__agreement-text {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
  &__recaptcha {
    grid-column: span 2 / span 2;
  }
  &__form-controls {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
}
</style>
